import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/pro-light-svg-icons";
import { displayCurrency, roundNumber } from "helpers/Helpers";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import "./OrderSummary.scss";
import { Badge, IBadgeProps } from "components/badge/Badge";
import classNames from "classnames";
import { ButtonNew as Button } from "components/buttonNew";

export interface ILineItem {
  src: string;
  productTitle: string;
  variantTitle: string;
  quantity: number;
  price: number;
}

export interface ITaxLine {
  title: string;
  price: number;
  rate: number;
}

interface IHeaderAction {
  label: string;
  action: () => void;
}

export interface IOrderSummaryProps {
  subtotal: number;
  total: number;
  taxLines?: ITaxLine[];
  discount: number;
  lineItems?: ILineItem[];
  togglableVisibility?: boolean;
  displayLineItemsWithoutToggle?: boolean;
  headerBadge?: IBadgeProps;
  headerAction?: IHeaderAction;
  children?: ReactNode;
  loyaltyPointsToEarn?: number;
}

export default function OrderSummary(props: IOrderSummaryProps) {
  const {
    subtotal,
    total,
    taxLines,
    discount,
    lineItems,
    togglableVisibility,
    displayLineItemsWithoutToggle,
    headerBadge,
    headerAction,
    children,
    loyaltyPointsToEarn,
  } = props;

  const [displayItems, setDisplayItems] = useState<boolean>(false);
  const [showOrderSummary, setShowOrderSummary] = useState<boolean>(false);
  const { t } = useTranslation();
  function renderOrderSummary() {
    return (
      <div className="order-summary">
        <div
          className={classNames("order-summary-header", {
            "order-summary-header-offset": !lineItems,
          })}
        >
          <div className="order-summary-heading-container">
            <h2 className="order-summary-heading">{t("Summary")}</h2>
            {headerBadge && <Badge {...headerBadge} />}
          </div>
          {headerAction && (
            <Button className="order-summary-header-action" type="link" onClick={headerAction.action}>
              {headerAction.label}
            </Button>
          )}
        </div>
        {lineItems ? (
          <>
            {!displayLineItemsWithoutToggle && (
              <button
                className="order-summary-no-toggle"
                onClick={() => setDisplayItems(previousDisplayItems => !previousDisplayItems)}
              >
                <span className="order-summary-no-toggle-primary">
                  <span className="order-summary-no-toggle-item-count">{`${lineItems.length} ${
                    lineItems.length === 1 ? "item" : "items"
                  }`}</span>
                  {displayItems ? (
                    <FontAwesomeIcon
                      className="order-summary-no-toggle-display-icon"
                      icon={faAngleUp}
                    ></FontAwesomeIcon>
                  ) : (
                    <FontAwesomeIcon
                      className="order-summary-no-toggle-display-icon"
                      icon={faAngleDown}
                    ></FontAwesomeIcon>
                  )}
                </span>
                <span className="order-summary-no-toggle-total">{displayCurrency("cad", subtotal)}</span>
              </button>
            )}
            <hr
              style={{
                margin: !displayLineItemsWithoutToggle && "0 0 20px 0",
                display: displayLineItemsWithoutToggle && lineItems.length === 0 && "none",
              }}
            />
            {displayLineItemsWithoutToggle || displayItems ? (
              <>
                <div className="order-summary-line-item-container">
                  {lineItems.map((lineItem, index) => {
                    return (
                      <React.Fragment key={index}>
                        <div className="order-summary-line-item-content">
                          <div className="order-summary-line-item-content-primary">
                            <div className="order-summary-line-item-quantity">{lineItem.quantity}</div>
                            <div>
                              <div className="order-summary-line-item-product-title">{lineItem.productTitle}</div>
                              {lineItem.variantTitle !== lineItem.productTitle && (
                                <div className="order-summary-line-item-variant-title">{lineItem.variantTitle}</div>
                              )}
                            </div>
                          </div>
                          <div className="order-summary-line-item-price">{displayCurrency("cad", lineItem.price)}</div>
                        </div>
                        {index < lineItems.length - 1 && <hr className="order-summary-line-item-divider" />}
                      </ React.Fragment>
                    );
                  })}
                </div>
                <hr></hr>
              </>
            ) : null}
          </>
        ) : null}
        <div className="order-summary-shopping-bag-values">
        {loyaltyPointsToEarn != null && loyaltyPointsToEarn !== 0 ? (
            <div>
              <span>{t("Loyalty Points")}</span>
              <span className="order-summary-shopping-bag-value order-summary-shopping-bag-points">
                {`+${loyaltyPointsToEarn} ${t("pts")}`}
              </span>
            </div>
          ) : null}

          <div>
            <span>{t("Subtotal")}</span>
            <span className="order-summary-shopping-bag-value">
              {subtotal !== undefined && subtotal !== null ? displayCurrency("cad", subtotal) : "Invalid"}
            </span>
          </div>
          {taxLines != null && taxLines.length > 0 ? (
            taxLines.map((taxLine, index) => {
              return (
                <div key={index}>
                  <span>
                    {taxLine.title} <span className="text-subdued">{taxLine.rate * 100}%</span>
                  </span>
                  <span className="order-summary-shopping-bag-value">{displayCurrency("cad", Number(roundNumber(taxLine.price, 2)))}</span>
                </div>
              );
            })
          ) : (
            <div>
              <span>{t("Tax")}</span>
              <span className="order-summary-shopping-bag-value">{displayCurrency("cad", 0)}</span>
            </div>
          )}

          {discount !== 0 ? (
            <div>
              <span>{t("Discount")}</span>
              <span className="order-summary-shopping-bag-value">
                {discount != null ? displayCurrency("cad", discount) : "Invalid"}
              </span>
            </div>
          ) : null}

          <div>
            <span className="order-summary-shopping-bag-total">{t("Total")}</span>
            <span className="order-summary-shopping-bag-total order-summary-shopping-bag-value">
              {total != null ? displayCurrency("cad", total) : "Invalid"}
            </span>
          </div>

          <hr className="order-summary-shopping-bag-total-dividing-line" />

          {children}
        </div>
      </div>
    );
  }

  return (
    <>
      {togglableVisibility ? (
        <>
          <h2 className="order-summary-visibility-display-toggle" onClick={() => setShowOrderSummary(prev => !prev)}>
            {t("Show Order Summary")}
            <FontAwesomeIcon
              className="order-summary-visibility-display-toggle-icon"
              icon={showOrderSummary ? faAngleUp : faAngleDown}
            />
          </h2>
          {showOrderSummary && <div className="order-summary-visibility-display-container">{renderOrderSummary()}</div>}
        </>
      ) : (
        <>{renderOrderSummary()}</>
      )}
    </>
  );
}
